import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useMentorSession } from '../../context/TrackerContext';
import {
  addJobPosting, getPlacementCoordinatorJobs, updateJob, closeJob, reopenJob, sendJobReminder,
  setApplicationStatus, getApplicationScreenshot,
  type AdminJobEntry, type JobActivity, type JobApplication, type JobBoardAuth,
} from '../../lib/api';
import {
  ReportHeader, HeaderButton, FilterCard, ToggleChip, SearchFilter, KpiTile, Pill, Notice, Loading, ServerOutdated,
  todayIST, addDays, daysBetween, fmtDay, inrShort, plural, type ActiveFilter, type Tone,
} from '../../components/ReportKit';

// The placement coordinators' Job Postings page. It shows every job the
// placement team has posted — this coordinator's, the other coordinators'
// and the admin's — and anyone can edit, close, reopen or remind students
// about any of them. The server logs each of those (job.activity) and the
// cards say who did what. The app's mentor_jobs_screen.dart mirrors this page.

const ALL_STUDENTS = 'All Students';
const COURSES = [ALL_STUDENTS, 'Full Stack Analytics', 'AIML', 'Data Science'];
const INTERVIEW_STATUSES = ['Applied', 'Interview Scheduled', 'Interview Done', 'Offer Received', 'Rejected'];
const STATUS_STYLE: Record<string, { bg: string; text: string }> = {
  'Applied':             { bg: '#eff6ff', text: '#1d4ed8' },
  'Interview Scheduled': { bg: '#fefce8', text: '#854d0e' },
  'Interview Done':      { bg: '#f0fdf4', text: '#166534' },
  'Offer Received':      { bg: '#dcfce7', text: '#15803d' },
  'Rejected':            { bg: '#fef2f2', text: '#b91c1c' },
};
const EMAIL_GAP_MS = 24 * 60 * 60 * 1000;   // students get one email per job a day — the post's or a reminder
const INK = '#2b2240';
const GOLD = '#F0B429';

type Scope = 'all' | 'mine';
type StatusFilter = 'open' | 'closed' | 'any';
type DetailTab = 'applied' | 'pending' | 'late' | 'history';

const tidy = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
const istDayOf = (iso: string) => new Date(new Date(iso).getTime() + 5.5 * 3600e3).toISOString().slice(0, 10);
// '6 Oct, 3:11 pm'
const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' });

const lastEntry = (job: AdminJobEntry, actions: JobActivity['action'][]) =>
  (job.activity ?? []).filter(a => actions.indexOf(a.action) >= 0).pop();

// The post's email or a reminder in the last 24 hours — no reminder until it's a day old
const recentEmail = (job: AdminJobEntry) => {
  const e = (job.activity ?? []).filter(a => (a.emailed ?? 0) > 0).pop();
  return e && Date.now() - new Date(e.at).getTime() < EMAIL_GAP_MS ? e : undefined;
};

function lastDateTag(job: AdminJobEntry, today: string): { tone: Tone; text: string } | null {
  if (!job.lastDate) return null;
  if (job.status !== 'Active') return { tone: 'neutral', text: `Last date ${fmtDay(job.lastDate, false)}` };
  const days = daysBetween(today, job.lastDate);
  if (days <= 0) return { tone: 'critical', text: 'Last day to apply today' };
  if (days === 1) return { tone: 'warning', text: 'Last day tomorrow' };
  if (days <= 3) return { tone: 'warning', text: `${days} days left · apply by ${fmtDay(job.lastDate, false)}` };
  return { tone: 'neutral', text: `Apply by ${fmtDay(job.lastDate, false)}` };
}

const HISTORY_LABEL: Record<JobActivity['action'], string> = {
  posted: 'Posted by', edited: 'Edited by', closed: 'Closed by', reopened: 'Reopened by', reminder: 'Reminder sent by', 'auto-closed': 'Closed automatically',
};

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">{children}</span>
);

const btn = 'px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap';

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────

const PlacementJobsPage: React.FC = () => {
  const session = useMentorSession();
  const me = session.mentorId;
  const who: JobBoardAuth = useMemo(() => ({ mentorId: me }), [me]);
  const today = todayIST();

  const [jobs, setJobs] = useState<AdminJobEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [outdated, setOutdated] = useState(false);

  const [scope, setScope] = useState<Scope>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [search, setSearch] = useState('');

  const [form, setForm] = useState<{ editing: AdminJobEntry | null } | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [tabs, setTabs] = useState<Record<string, DetailTab>>({});
  const [working, setWorking] = useState<string | null>(null);   // the job or application being changed
  const [offerFor, setOfferFor] = useState<{ job: AdminJobEntry; app: JobApplication } | null>(null);
  const [reopening, setReopening] = useState<AdminJobEntry | null>(null);
  const [screenshot, setScreenshot] = useState<JobApplication | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (quiet) setBusy(true); else setLoading(true);
    try {
      const res = await getPlacementCoordinatorJobs(me);
      const list = res.jobs ?? [];
      // An older server sends only this coordinator's jobs, without applicants
      setOutdated(list.some(j => !Array.isArray(j.applications)));
      setJobs(list.map(j => ({
        ...j,
        applications: j.applications ?? [],
        notAppliedStudents: j.notAppliedStudents ?? [],
        appliedCount: j.appliedCount ?? j.applicationCount ?? 0,
        notAppliedCount: j.notAppliedCount ?? 0,
        totalEligible: j.totalEligible ?? 0,
        targetCourses: j.targetCourses ?? [],
        activity: j.activity ?? [],
      })));
    } catch (e: any) {
      toast.error(e.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
      setBusy(false);
    }
  }, [me]);

  useEffect(() => { load(); }, [load]);

  // ── What's showing ──
  const isMine = (j: AdminJobEntry) => j.postedBy === me;
  const scoped = scope === 'mine' ? jobs.filter(isMine) : jobs;
  const q = search.trim().toLowerCase();
  const visible = scoped.filter(j =>
    (statusFilter === 'any' || (statusFilter === 'open') === (j.status === 'Active')) &&
    (!q || [j.title, j.company, j.postedByName].some(s => (s || '').toLowerCase().includes(q))));

  const openJobs = scoped.filter(j => j.status === 'Active');
  const apps = scoped.reduce<JobApplication[]>((all, j) => all.concat(j.applications), []);
  const closingSoon = openJobs.filter(j => j.lastDate && daysBetween(today, j.lastDate) <= 3).length;
  const stillToApply = openJobs.reduce((n, j) => n + j.notAppliedCount, 0);
  const interviews = apps.filter(a => a.interviewStatus === 'Interview Scheduled').length;
  const offers = apps.filter(a => a.interviewStatus === 'Offer Received');
  const bestOffer = offers.reduce((m, a) => Math.max(m, a.offerSalary || 0), 0);

  const active: ActiveFilter[] = [];
  if (scope === 'mine') active.push({ label: 'Posted by me', onRemove: () => setScope('all') });
  if (statusFilter !== 'any') active.push({ label: statusFilter === 'open' ? 'Open jobs' : 'Closed jobs', onRemove: () => setStatusFilter('any') });
  if (q) active.push({ label: `“${search.trim()}”`, onRemove: () => setSearch('') });
  const clearFilters = () => { setScope('all'); setStatusFilter('any'); setSearch(''); };

  // ── Actions ──
  const patchApp = (jobId: string, appId: string, patch: Partial<JobApplication>) =>
    setJobs(prev => prev.map(j => j._id !== jobId ? j
      : { ...j, applications: j.applications.map(a => a._id === appId ? { ...a, ...patch } : a) }));

  async function changeStatus(job: AdminJobEntry, app: JobApplication, status: string, salary?: number) {
    setWorking(app._id);
    try {
      const res = await setApplicationStatus(who, app._id, status, salary);
      patchApp(job._id, app._id, res.application ?? { interviewStatus: status });
      toast.success(`${app.studentName} — ${status}`);
      setOfferFor(null);
    } catch (e: any) {
      toast.error(e.message || 'Failed to update the status');
    } finally {
      setWorking(null);
    }
  }

  function pickStatus(job: AdminJobEntry, app: JobApplication, status: string) {
    if (status === 'Offer Received') setOfferFor({ job, app });   // asks for the salary first
    else changeStatus(job, app, status);
  }

  async function runJobAction(job: AdminJobEntry, action: () => Promise<unknown>, done: string, failed: string) {
    setWorking(job._id);
    try {
      await action();
      toast.success(done);
      setReopening(null);
    } catch (e: any) {
      toast.error(e.message || failed);
    } finally {
      setWorking(null);
      load(true);   // someone else may have changed it too
    }
  }

  function handleClose(job: AdminJobEntry) {
    if (!window.confirm(`Close "${job.title}" at ${job.company}?\n\nStudents won't see it on their job board any more. Anyone can reopen it later.`)) return;
    runJobAction(job, () => closeJob(who, job._id), 'Job closed', 'Failed to close the job');
  }

  function handleReopen(job: AdminJobEntry) {
    if (job.lastDate && job.lastDate < today) setReopening(job);   // needs a new last date
    else runJobAction(job, () => reopenJob(who, job._id), 'Job reopened', 'Failed to reopen the job');
  }

  function handleRemind(job: AdminJobEntry) {
    const n = job.notAppliedStudents.filter(s => s.email).length;
    if (!window.confirm(`Email a reminder to ${plural(n, 'student')} who haven't applied for "${job.title}" at ${job.company}?\n\nStudents get at most one email a day about each job.`)) return;
    setWorking(job._id);
    sendJobReminder(who, job._id)
      .then(r => toast.success(`Reminder sent to ${plural(r.sent, 'student')}`))
      .catch(e => toast.error(e.message || 'Failed to send the reminder'))
      .finally(() => { setWorking(null); load(true); });
  }

  function openForm(editing: AdminJobEntry | null) {
    setForm({ editing });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <TrackerLayout>
      <div className="max-w-5xl mx-auto space-y-5">
        <ReportHeader
          icon="💼"
          title="Job Postings"
          intro="Every job the placement team has posted — yours, the other coordinators' and the admin's. Anyone can edit, close, reopen or remind students about any job, and each card shows who did what."
          actions={<>
            <HeaderButton onClick={() => load(true)} disabled={loading || busy}>↻ Refresh</HeaderButton>
            <button
              onClick={() => (form && !form.editing ? setForm(null) : openForm(null))}
              className="px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-opacity hover:opacity-90"
              style={{ background: GOLD, color: INK }}>
              {form && !form.editing ? 'Cancel' : '+ Post a Job'}
            </button>
          </>}
        />

        {outdated && <ServerOutdated />}

        {form && (
          <JobForm
            key={form.editing?._id ?? 'new'}
            jobs={jobs}
            editing={form.editing}
            mentorId={me}
            onCancel={() => setForm(null)}
            onSaved={() => { setForm(null); load(true); }}
          />
        )}

        {loading ? <Loading text="Loading jobs…" /> : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiTile label="Open jobs" value={openJobs.length}
                hint={closingSoon === 0 ? 'None closing within 3 days'
                  : `${plural(closingSoon, 'job')} ${closingSoon === 1 ? 'closes' : 'close'} within 3 days`} />
              <KpiTile label="Applications" value={apps.length}
                hint={`${plural(stillToApply, 'eligible student')} still to apply on open jobs`} />
              <KpiTile label="Interviews scheduled" value={interviews} hint="Applicants waiting for an interview" />
              <KpiTile label="Offers received" value={offers.length}
                hint={bestOffer ? `Best offer ${inrShort(bestOffer)} a year` : 'Add the salary when you mark an offer'} />
            </div>

            <FilterCard
              showing={`Showing ${visible.length} of ${plural(jobs.length, 'job')}${scope === 'mine' ? '' : ' from the whole team'}`}
              active={active}
              onClearAll={clearFilters}
              busy={busy}>
              <div className="flex flex-wrap gap-x-6 gap-y-4 items-end">
                <div>
                  <FieldLabel>Posted by</FieldLabel>
                  <div className="flex flex-wrap gap-1.5">
                    <ToggleChip on={scope === 'all'} onClick={() => setScope('all')} count={jobs.length}>Everyone</ToggleChip>
                    <ToggleChip on={scope === 'mine'} onClick={() => setScope('mine')} count={jobs.filter(isMine).length}>Me</ToggleChip>
                  </div>
                </div>
                <div>
                  <FieldLabel>Status</FieldLabel>
                  <div className="flex flex-wrap gap-1.5">
                    <ToggleChip on={statusFilter === 'open'} onClick={() => setStatusFilter('open')} count={openJobs.length}>Open</ToggleChip>
                    <ToggleChip on={statusFilter === 'closed'} onClick={() => setStatusFilter('closed')} count={scoped.length - openJobs.length}>Closed</ToggleChip>
                    <ToggleChip on={statusFilter === 'any'} onClick={() => setStatusFilter('any')}>Both</ToggleChip>
                  </div>
                </div>
                <SearchFilter label="Search" value={search} onChange={setSearch} placeholder="Job, company or who posted it" />
              </div>
            </FilterCard>

            {jobs.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                <div className="text-4xl mb-3">💼</div>
                <p className="font-bold text-gray-600">No jobs posted yet</p>
                <p className="text-xs text-gray-400 mt-1">Click “+ Post a Job” to add the first one.</p>
              </div>
            ) : visible.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                <p className="font-bold text-gray-600">No jobs match these filters</p>
                <button onClick={clearFilters} className="mt-3 text-xs font-bold underline underline-offset-2 text-gray-500 hover:text-gray-800">Show all jobs</button>
              </div>
            ) : (
              <div className="space-y-4">
                {visible.map(job => (
                  <JobCard
                    key={job._id}
                    job={job}
                    me={me}
                    today={today}
                    open={expanded === job._id}
                    tab={tabs[job._id] ?? 'applied'}
                    working={working}
                    onToggle={() => setExpanded(expanded === job._id ? null : job._id)}
                    onTab={t => { setTabs(prev => ({ ...prev, [job._id]: t })); setExpanded(job._id); }}
                    onEdit={() => openForm(job)}
                    onClose={() => handleClose(job)}
                    onReopen={() => handleReopen(job)}
                    onRemind={() => handleRemind(job)}
                    onPickStatus={(app, status) => pickStatus(job, app, status)}
                    onEditOffer={app => setOfferFor({ job, app })}
                    onScreenshot={app => setScreenshot(app)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {offerFor && (
        <OfferModal
          job={offerFor.job}
          app={offerFor.app}
          saving={working === offerFor.app._id}
          onCancel={() => setOfferFor(null)}
          onSave={salary => changeStatus(offerFor.job, offerFor.app, 'Offer Received', salary)}
        />
      )}
      {reopening && (
        <ReopenModal
          job={reopening}
          today={today}
          saving={working === reopening._id}
          onCancel={() => setReopening(null)}
          onReopen={lastDate => runJobAction(reopening, () => reopenJob(who, reopening._id, lastDate), 'Job reopened', 'Failed to reopen the job')}
        />
      )}
      {screenshot && <ScreenshotModal who={who} app={screenshot} onClose={() => setScreenshot(null)} />}
    </TrackerLayout>
  );
};

// ── JOB CARD ──────────────────────────────────────────────────────────────────

const JobCard: React.FC<{
  job: AdminJobEntry; me: string; today: string; open: boolean; tab: DetailTab; working: string | null;
  onToggle: () => void; onTab: (t: DetailTab) => void;
  onEdit: () => void; onClose: () => void; onReopen: () => void; onRemind: () => void;
  onPickStatus: (app: JobApplication, status: string) => void;
  onEditOffer: (app: JobApplication) => void;
  onScreenshot: (app: JobApplication) => void;
}> = ({ job, me, today, open, tab, working, onToggle, onTab, onEdit, onClose, onReopen, onRemind, onPickStatus, onEditOffer, onScreenshot }) => {
  const isOpen = job.status === 'Active';
  const tag = lastDateTag(job, today);
  const eligibleApplied = Math.max(0, job.totalEligible - job.notAppliedCount);
  const pct = job.totalEligible > 0 ? Math.round((eligibleApplied / job.totalEligible) * 100) : 0;
  const closedBy = isOpen ? undefined : lastEntry(job, ['closed', 'auto-closed']);
  const editedBy = lastEntry(job, ['edited']);
  const lastReminder = lastEntry(job, ['reminder']);
  const emailedToday = recentEmail(job);
  const remindable = job.notAppliedStudents.filter(s => s.email).length;
  const late = isOpen && job.threeDaysOld ? job.notAppliedStudents : [];
  const busy = working === job._id;

  const remindLabel = job.notAppliedCount === 0 ? 'Everyone applied'
    : remindable === 0 ? 'No emails to remind'
    : emailedToday ? 'Emailed today'
    : `Remind ${plural(remindable, 'student')}`;
  const remindTitle = !emailedToday ? "Email the students who haven't applied yet"
    : emailedToday.action === 'posted' ? `Students were emailed when ${emailedToday.byName} posted this job (${fmtWhen(emailedToday.at)}). A reminder can go a day after that.`
    : `${emailedToday.byName} sent a reminder on ${fmtWhen(emailedToday.at)} — one email a day per job.`;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-gray-900">{job.title}</h3>
              <Pill tone={isOpen ? 'good' : 'neutral'}>{isOpen ? 'Open' : 'Closed'}</Pill>
              {tag && <Pill tone={tag.tone} icon={false}>{tag.text}</Pill>}
              {late.length > 0 && (
                <button onClick={() => onTab('late')} className="focus:outline-none" title="See who hasn't applied">
                  <Pill tone="critical">{late.length} not applied after 3 days</Pill>
                </button>
              )}
            </div>
            <p className="text-sm font-medium text-gray-600 mt-0.5">{job.company}</p>
            <p className="text-xs text-gray-400 mt-1">
              Posted by <span className="font-semibold text-gray-600">{job.postedByName || 'Unknown'}{job.postedBy === me ? ' (you)' : ''}</span>
              {' · '}{fmtDay(istDayOf(job.createdAt))}
              {editedBy && <> · last edited by <span className="font-semibold text-gray-600">{editedBy.byName}</span> · {fmtDay(istDayOf(editedBy.at), false)}</>}
            </p>
            {!isOpen && (
              <p className="text-xs mt-1 font-medium" style={{ color: '#92400e' }}>
                {closedBy?.action === 'auto-closed' ? `Closed automatically after the last date · ${fmtDay(istDayOf(closedBy.at), false)}`
                  : closedBy ? `Closed by ${closedBy.byName} · ${fmtWhen(closedBy.at)}`
                  : 'Closed'}
              </p>
            )}
            <div className="flex flex-wrap gap-1 mt-2">
              {job.targetCourses.map(c => (
                <span key={c} className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full">{c}</span>
              ))}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-2xl font-black" style={{ color: INK }}>{pct}%</p>
            <p className="text-xs text-gray-500 whitespace-nowrap">{eligibleApplied} of {job.totalEligible} applied</p>
          </div>
        </div>

        <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all"
            style={{ width: `${pct}%`, backgroundColor: pct >= 70 ? '#22c55e' : pct >= 40 ? GOLD : '#ef4444' }} />
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-4">
          <button onClick={onToggle} className={`${btn} border-gray-300 text-gray-700 hover:bg-gray-50`}>
            {open ? 'Hide details' : `Details · ${plural(job.appliedCount, 'applicant')}`}
          </button>
          <button onClick={onEdit} disabled={busy} className={`${btn} border-gray-300 text-gray-700 hover:bg-gray-50`}>Edit</button>
          {isOpen && (
            <button onClick={onRemind} disabled={busy || !!emailedToday || remindable === 0} title={remindTitle}
              className={`${btn} border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100`}>
              {busy ? '…' : remindLabel}
            </button>
          )}
          {isOpen ? (
            <button onClick={onClose} disabled={busy} className={`${btn} border-red-200 text-red-600 bg-red-50 hover:bg-red-100`}>
              {busy ? '…' : 'Close job'}
            </button>
          ) : (
            <button onClick={onReopen} disabled={busy} className={`${btn} border-green-200 text-green-700 bg-green-50 hover:bg-green-100`}>
              {busy ? '…' : 'Reopen'}
            </button>
          )}
          {lastReminder && (
            <span className="text-[11px] text-gray-400">Last reminder by {lastReminder.byName} · {fmtWhen(lastReminder.at)}</span>
          )}
        </div>
      </div>

      {open && (
        <JobDetails job={job} tab={tab} late={late} working={working} onTab={onTab}
          onPickStatus={onPickStatus} onEditOffer={onEditOffer} onScreenshot={onScreenshot}
          onRemind={onRemind} canRemind={isOpen && !busy && !emailedToday && remindable > 0} />
      )}
    </div>
  );
};

// ── JOB DETAILS (applicants, who still has to apply, history) ─────────────────

const JobDetails: React.FC<{
  job: AdminJobEntry; tab: DetailTab; late: AdminJobEntry['notAppliedStudents']; working: string | null;
  onTab: (t: DetailTab) => void;
  onPickStatus: (app: JobApplication, status: string) => void;
  onEditOffer: (app: JobApplication) => void;
  onScreenshot: (app: JobApplication) => void;
  onRemind: () => void; canRemind: boolean;
}> = ({ job, tab, late, working, onTab, onPickStatus, onEditOffer, onScreenshot, onRemind, canRemind }) => {
  const history = (job.activity ?? []).slice().reverse();
  if (!history.some(e => e.action === 'posted')) history.push({ action: 'posted', byName: job.postedByName, at: job.createdAt });   // jobs from before the log
  const current: DetailTab = tab === 'late' && late.length === 0 ? 'applied' : tab;

  const tabList: { key: DetailTab; label: string }[] = [
    { key: 'applied', label: `Applied (${job.applications.length})` },
    { key: 'pending', label: `Still to apply (${job.notAppliedCount})` },
    ...(late.length > 0 ? [{ key: 'late' as DetailTab, label: `⚠ 3+ days (${late.length})` }] : []),
    { key: 'history', label: `History (${history.length})` },
  ];

  const studentRow = (s: AdminJobEntry['notAppliedStudents'][number], warn: boolean) => (
    <div key={s.studentId} className={`rounded-lg border px-4 py-2.5 flex items-center justify-between gap-3 ${warn ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-800">{s.name}</p>
        <p className="text-xs text-gray-400 break-all">{s.email || 'No email'} · {s.course || '—'}</p>
      </div>
      {!s.email && <Pill tone="neutral" icon={false}>No email</Pill>}
    </div>
  );

  return (
    <div className="border-t border-gray-100 bg-gray-50">
      {(job.description || job.jobLink) && (
        <div className="px-5 pt-4 space-y-1.5">
          {job.description && <p className="text-sm text-gray-600 whitespace-pre-line">{job.description}</p>}
          {job.jobLink && (
            <a href={job.jobLink} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline break-all">{job.jobLink}</a>
          )}
        </div>
      )}

      <div className="flex border-b border-gray-200 px-3 mt-2 overflow-x-auto">
        {tabList.map(t => (
          <button key={t.key} onClick={() => onTab(t.key)}
            className="px-3 py-2.5 text-xs font-semibold border-b-2 whitespace-nowrap transition-colors"
            style={{ borderColor: current === t.key ? INK : 'transparent', color: current === t.key ? INK : '#6b7280' }}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-5 space-y-2">
        {current === 'applied' && (job.applications.length === 0
          ? <p className="text-sm text-gray-400">No applications yet.</p>
          : job.applications.map(app => {
              const status = app.interviewStatus ?? 'Applied';
              const s = STATUS_STYLE[status] ?? STATUS_STYLE['Applied'];
              return (
                <div key={app._id} className="bg-white rounded-lg border border-gray-200 px-4 py-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800">{app.studentName}</p>
                      <p className="text-xs text-gray-400 break-all">{app.studentEmail || 'No email'} · {app.course || '—'}</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        Applied {fmtDay(istDayOf(app.createdAt), false)}
                        {app.statusUpdatedByName && <> · status set by {app.statusUpdatedByName}{app.statusUpdatedAt ? ` · ${fmtDay(istDayOf(app.statusUpdatedAt), false)}` : ''}</>}
                      </p>
                      {status === 'Offer Received' && (
                        <p className="text-xs mt-1 font-semibold text-green-700">
                          Offer {app.offerSalary ? `${inrShort(app.offerSalary)} a year` : '— salary not added'}
                          {' · '}
                          <button onClick={() => onEditOffer(app)} className="underline underline-offset-2">{app.offerSalary ? 'change' : 'add salary'}</button>
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {app.hasScreenshot && (
                        <button onClick={() => onScreenshot(app)} className="text-xs text-blue-600 hover:underline">Screenshot</button>
                      )}
                      <select
                        value={status}
                        disabled={working === app._id}
                        onChange={e => onPickStatus(app, e.target.value)}
                        aria-label={`Interview status for ${app.studentName}`}
                        className="text-xs px-2 py-1.5 rounded-lg border font-semibold outline-none cursor-pointer disabled:opacity-60"
                        style={{ backgroundColor: s.bg, color: s.text, borderColor: s.text + '40' }}>
                        {INTERVIEW_STATUSES.map(st => <option key={st} value={st}>{st}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              );
            }))}

        {current === 'pending' && (job.notAppliedStudents.length === 0
          ? <p className="text-sm text-gray-400">Every eligible student has applied.</p>
          : job.notAppliedStudents.map(s => studentRow(s, false)))}

        {current === 'late' && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <p className="text-xs font-semibold text-red-600">These students haven't applied, and the job was posted more than 3 days ago.</p>
              <button onClick={onRemind} disabled={!canRemind} className={`${btn} border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100`}>
                Send reminder
              </button>
            </div>
            {late.map(s => studentRow(s, true))}
          </>
        )}

        {current === 'history' && history.map((e, i) => (
          <div key={i} className="flex items-start gap-3 text-sm">
            <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ background: e.action === 'closed' || e.action === 'auto-closed' ? '#ef4444' : e.action === 'reminder' ? GOLD : INK }} />
            <div>
              <p className="text-gray-700">
                <span className="font-semibold">{HISTORY_LABEL[e.action]}{e.action === 'auto-closed' ? '' : ` ${e.byName || 'someone'}`}</span>
                <span className="text-gray-400"> · {fmtWhen(e.at)}</span>
              </p>
              {e.detail && <p className="text-xs text-gray-500">{e.detail}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── POST / EDIT FORM ──────────────────────────────────────────────────────────

const JobForm: React.FC<{
  jobs: AdminJobEntry[]; editing: AdminJobEntry | null; mentorId: string;
  onCancel: () => void; onSaved: () => void;
}> = ({ jobs, editing, mentorId, onCancel, onSaved }) => {
  const today = todayIST();
  const [f, setF] = useState(() => ({
    title: editing?.title ?? '',
    company: editing?.company ?? '',
    description: editing?.description ?? '',
    jobLink: editing?.jobLink ?? '',
    targetCourses: editing ? editing.targetCourses.slice() : [] as string[],
    lastDate: editing?.lastDate ?? '',
    notify: true,
  }));
  const [saving, setSaving] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF(prev => ({ ...prev, [k]: v }));

  // Courses from older jobs that aren't in today's list stay pickable
  const courseChoices = COURSES.concat(f.targetCourses.filter(c => COURSES.indexOf(c) < 0));

  function toggleCourse(course: string) {
    setF(prev => {
      if (course === ALL_STUDENTS) return { ...prev, targetCourses: prev.targetCourses.indexOf(ALL_STUDENTS) >= 0 ? [] : [ALL_STUDENTS] };
      const without = prev.targetCourses.filter(c => c !== ALL_STUDENTS && c !== course);
      return { ...prev, targetCourses: prev.targetCourses.indexOf(course) >= 0 ? without : without.concat(course) };
    });
  }

  // The same job already open — the server refuses it, so say so while typing
  const checkDup = !editing || editing.status === 'Active';
  const others = jobs.filter(j => j.status === 'Active' && j._id !== editing?._id);
  const dup = checkDup && f.title.trim() && f.company.trim()
    ? others.find(j => tidy(j.title) === tidy(f.title) && tidy(j.company) === tidy(f.company))
    : undefined;
  const sameCompany = !dup && f.company.trim() ? others.filter(j => tidy(j.company) === tidy(f.company)) : [];
  const dateChanged = f.lastDate !== (editing?.lastDate ?? '');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!f.title.trim() || !f.company.trim()) { toast.error('Title and company are required'); return; }
    if (f.targetCourses.length === 0) { toast.error('Select at least one target course'); return; }
    if (dup) { toast.error('This job is already open — edit or close that one instead'); return; }
    if (f.lastDate && dateChanged && f.lastDate < today) { toast.error('The last date to apply cannot be in the past'); return; }
    setSaving(true);
    try {
      const fields = {
        title: f.title.trim(), company: f.company.trim(), description: f.description.trim(),
        jobLink: f.jobLink.trim(), targetCourses: f.targetCourses, lastDate: f.lastDate,
      };
      if (editing) {
        const r = await updateJob({ mentorId }, editing._id, fields);
        toast.success(r.changed && r.changed.length === 0 ? 'Nothing changed' : 'Changes saved');
      } else {
        const r = await addJobPosting(mentorId, fields.title, fields.company, fields.description, fields.jobLink, fields.targetCourses, f.notify, fields.lastDate);
        toast.success(!f.notify ? 'Job posted — no emails sent'
          : r.emailed === undefined ? 'Job posted and students notified'
          : `Job posted — ${plural(r.emailed, 'student')} emailed`);
      }
      onSaved();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save the job');
      setSaving(false);
    }
  }

  const input = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#2b2240]';

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-bold" style={{ color: INK }}>{editing ? 'Edit job' : 'New job opening'}</h2>
          {editing && (
            <p className="text-xs text-gray-400 mt-0.5">
              Posted by {editing.postedByName || 'Unknown'}{editing.postedBy === mentorId ? ' (you)' : ''}. Your changes are logged on the job.
            </p>
          )}
        </div>
        <button onClick={onCancel} className="text-xs font-semibold text-gray-500 hover:text-gray-800">Cancel</button>
      </div>
      <form onSubmit={submit} noValidate className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <FieldLabel>Job title *</FieldLabel>
            <input type="text" value={f.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Data Analyst Intern" className={input} />
          </label>
          <label className="block">
            <FieldLabel>Company *</FieldLabel>
            <input type="text" value={f.company} onChange={e => set('company', e.target.value)} placeholder="e.g. Infosys" className={input} />
          </label>
        </div>

        {dup && (
          <Notice tone="critical">
            “{dup.title}” at {dup.company} is already open — posted by {dup.postedByName || 'someone'} on {fmtDay(istDayOf(dup.createdAt))}. Edit or close that one instead.
          </Notice>
        )}
        {sameCompany.length > 0 && (
          <Notice tone="neutral">
            Also open at {sameCompany[0].company}: {sameCompany.map(j => `${j.title} (${j.postedByName || 'Unknown'})`).join(', ')}
          </Notice>
        )}

        <label className="block">
          <FieldLabel>Job link</FieldLabel>
          <input type="url" value={f.jobLink} onChange={e => set('jobLink', e.target.value)} placeholder="https://..." className={input} />
        </label>

        <label className="block">
          <FieldLabel>Description</FieldLabel>
          <textarea value={f.description} onChange={e => set('description', e.target.value)} rows={3}
            placeholder="Job details, requirements, salary range…" className={`${input} resize-none`} />
        </label>

        <div>
          <FieldLabel>Target courses *</FieldLabel>
          <div className="flex flex-wrap gap-2">
            {courseChoices.map(c => {
              const on = f.targetCourses.indexOf(c) >= 0;
              return (
                <button key={c} type="button" onClick={() => toggleCourse(c)} aria-pressed={on}
                  className="px-3 py-1.5 rounded-full text-xs font-medium border transition-colors"
                  style={on ? { backgroundColor: INK, color: '#fff', borderColor: INK } : { backgroundColor: '#fff', color: '#374151', borderColor: '#d1d5db' }}>
                  {c}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <FieldLabel>Last date to apply</FieldLabel>
          <div className="flex flex-wrap items-center gap-2">
            <input type="date" value={f.lastDate} min={dateChanged ? today : undefined} max={addDays(today, 365)}
              onChange={e => set('lastDate', e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#2b2240]" />
            {f.lastDate && <button type="button" onClick={() => set('lastDate', '')} className="text-xs font-semibold text-gray-500 underline underline-offset-2">Remove</button>}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">Optional. The job closes by itself after this date.</p>
        </div>

        {!editing && (
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
            <input type="checkbox" checked={f.notify} onChange={e => set('notify', e.target.checked)} className="w-4 h-4 accent-purple-700" />
            Email the eligible students now
          </label>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <button type="submit" disabled={saving || !!dup}
            className="px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60"
            style={{ backgroundColor: GOLD, color: INK }}>
            {saving ? 'Saving…' : editing ? 'Save changes' : f.notify ? 'Post Job & Notify Students' : 'Post Job'}
          </button>
          <button type="button" onClick={onCancel} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
        </div>
      </form>
    </div>
  );
};

// ── MODALS ────────────────────────────────────────────────────────────────────

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }> = ({ title, onClose, children, wide }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label={title}
        className={`relative bg-white rounded-2xl shadow-2xl w-full ${wide ? 'max-w-3xl' : 'max-w-md'} max-h-[90vh] flex flex-col overflow-hidden`}>
        <div className="px-5 py-4 flex items-center justify-between gap-3" style={{ background: 'linear-gradient(135deg,#2b2240,#4e3a69)' }}>
          <p className="text-sm font-bold text-white">{title}</p>
          <button onClick={onClose} aria-label="Close" className="w-7 h-7 rounded-full text-white/70 hover:text-white hover:bg-white/10">×</button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

const OfferModal: React.FC<{
  job: AdminJobEntry; app: JobApplication; saving: boolean; onCancel: () => void; onSave: (salary: number) => void;
}> = ({ job, app, saving, onCancel, onSave }) => {
  const [salary, setSalary] = useState(app.offerSalary ? String(app.offerSalary) : '');
  const amount = Math.max(0, Math.round(Number(salary) || 0));
  return (
    <Modal title={`Offer for ${app.studentName}`} onClose={onCancel}>
      <p className="text-sm text-gray-600 mb-4">{job.title} at {job.company}</p>
      <label className="block">
        <FieldLabel>Salary offered (₹ a year) — optional</FieldLabel>
        <input type="number" min={0} step={1000} value={salary} onChange={e => setSalary(e.target.value)} placeholder="e.g. 350000" autoFocus
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#2b2240]" />
      </label>
      <p className="text-xs text-gray-400 mt-1.5">{amount > 0 ? `${inrShort(amount)} a year. ` : ''}The offer also shows on the student's Placement Tracker record.</p>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
        <button onClick={() => onSave(amount)} disabled={saving}
          className="px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-60" style={{ background: INK }}>
          {saving ? 'Saving…' : 'Save offer'}
        </button>
      </div>
    </Modal>
  );
};

const ReopenModal: React.FC<{
  job: AdminJobEntry; today: string; saving: boolean; onCancel: () => void; onReopen: (lastDate: string) => void;
}> = ({ job, today, saving, onCancel, onReopen }) => {
  const [date, setDate] = useState(addDays(today, 7));
  const valid = date >= today && date <= addDays(today, 365);
  return (
    <Modal title="Reopen job" onClose={onCancel}>
      <p className="text-sm text-gray-600">
        The last date to apply for <strong>{job.title}</strong> at {job.company} ({fmtDay(job.lastDate ?? '')}) has passed.
        Pick a new last date, or reopen it without one.
      </p>
      <label className="block mt-4">
        <FieldLabel>New last date to apply</FieldLabel>
        <input type="date" value={date} min={today} max={addDays(today, 365)} onChange={e => setDate(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#2b2240]" />
      </label>
      <div className="flex flex-wrap justify-end gap-2 mt-5">
        <button onClick={() => onReopen('')} disabled={saving} className="px-4 py-2 rounded-lg text-sm border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-60">
          Reopen without a last date
        </button>
        <button onClick={() => onReopen(date)} disabled={saving || !valid}
          className="px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-60" style={{ background: INK }}>
          {saving ? 'Reopening…' : valid ? `Reopen until ${fmtDay(date, false)}` : 'Pick a date'}
        </button>
      </div>
    </Modal>
  );
};

const ScreenshotModal: React.FC<{ who: JobBoardAuth; app: JobApplication; onClose: () => void }> = ({ who, app, onClose }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    getApplicationScreenshot(who, app._id)
      .then(r => {
        if (!live) return;
        if (r.screenshotBase64) setSrc(r.screenshotBase64);
        else setError('No screenshot was uploaded with this application.');
      })
      .catch(e => { if (live) setError(e.message || 'Could not load the screenshot'); });
    return () => { live = false; };
  }, [who, app._id]);

  return (
    <Modal title={`${app.studentName} — application screenshot`} onClose={onClose} wide>
      {error ? <p className="text-sm text-red-600">{error}</p>
        : !src ? <Loading text="Loading screenshot…" />
        : (
          <div className="space-y-3">
            <img src={src} alt={`Application proof uploaded by ${app.studentName}`} className="w-full h-auto rounded-lg border border-gray-200" />
            <a href={src} download={app.fileName || 'screenshot.png'} className="inline-block text-xs font-semibold text-blue-600 hover:underline">Download</a>
          </div>
        )}
    </Modal>
  );
};

export default PlacementJobsPage;
